import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Clock,
  FileText,
  FolderOpen,
  Plus,
  Settings,
  CheckCircle,
  Activity,
  BarChart2,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import api from "../../../services/api";
import "../../admin/Dashboard/Dashboard.css";

const Dashboard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectStats, setProjectStats] = useState({
    total: 0,
    completed: 0,
    active: 0,
  });
  const [recentApprovalRequests, setRecentApprovalRequests] = useState([]);
  const [loadingApprovalRequests, setLoadingApprovalRequests] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserProjects = async () => {
      try {
        setLoading(true);
        const userData = JSON.parse(localStorage.getItem("user"));
        if (!userData || !userData.id) return;
        const response = await api.get(`/projects/user/${userData.id}`);

        if (response.data && response.data.projects) {
          const sortedProjects = response.data.projects.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );
          setProjects(sortedProjects);

          const completed = sortedProjects.filter(
            (p) => p.status === "completed"
          ).length;
          const active = sortedProjects.filter(
            (p) => p.status === "active"
          ).length;
          const total = sortedProjects.length;

          setProjectStats({
            completed,
            active,
            total,
          });
        }
      } catch (error) {
        console.error("Error fetching user projects:", error);
        if (error.response) {
          console.error("Response status:", error.response.status);
          console.error("Response data:", error.response.data);
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchApprovalRequests = async () => {
      try {
        setLoadingApprovalRequests(true);
        const userData = JSON.parse(localStorage.getItem("user"));
        if (!userData || !userData.id) return;

        const response = await api.get(
          `/api/approval-requests/user/${userData.id}`
        );

        if (response.data && response.data.requests) {
          setRecentApprovalRequests(response.data.requests.slice(0, 5));
        }
      } catch (error) {
        console.error("Error fetching approval requests:", error);
        if (error.response) {
          console.error("Response status:", error.response.status);
          console.error("Response data:", error.response.data);
        }
        setRecentApprovalRequests([]);
      } finally {
        setLoadingApprovalRequests(false);
      }
    };

    fetchUserProjects();
    fetchApprovalRequests();
  }, []);

  const getCurrentDate = () => {
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date().toLocaleDateString("en-US", options);
  };

  const user = JSON.parse(localStorage.getItem("user"));
  const name = user ? user.name : "User";

  const handleProjectClick = (projectId) => {
    navigate(`/project_associate/projects/${projectId}`);
  };

  const quickActionLinks = [
    {
      title: "Create New Project",
      icon: <Plus size={20} />,
      path: "/project_associate/create-project",
      color: "#3182ce",
    },
    {
      title: "Manage Projects",
      icon: <FolderOpen size={20} />,
      path: "/project_associate/manage-projects",
      color: "#38a169",
    },
    {
      title: "My Profile",
      icon: <Settings size={20} />,
      path: "/project_associate/profile",
      color: "#805ad5",
    },
  ];

  return (
    <div className="dashboard-container">
      <Navbar role="project_associate" />

      <div className="dashboard-content">
        <div className="dashboard-header">
          <div className="header-info">
            <h1>Project Associate Dashboard</h1>
            <p className="date-display">{getCurrentDate()}</p>
          </div>
          <div className="user-welcome">
            <h2>Welcome back, {name}</h2>
          </div>
        </div>

        <div className="dashboard-grid">
          {/* Stats Overview - Important analytics first */}
          <div className="stats-overview">
            <h2 className="section-title">
              <BarChart2 size={20} className="section-icon" />
              Projects Overview
            </h2>
            <div className="stats-cards">
              <div className="stat-card active">
                <div className="stat-icon">
                  <Activity size={24} />
                </div>
                <div className="stat-info">
                  <h3>Active Projects</h3>
                  <p>{projectStats.active}</p>
                </div>
              </div>

              <div className="stat-card completed">
                <div className="stat-icon">
                  <CheckCircle size={24} />
                </div>
                <div className="stat-info">
                  <h3>Completed Projects</h3>
                  <p>{projectStats.completed}</p>
                </div>
              </div>

              <div className="stat-card total">
                <div className="stat-icon">
                  <FolderOpen size={24} />
                </div>
                <div className="stat-info">
                  <h3>Total Projects</h3>
                  <p>{projectStats.total}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Two-column layout for main content */}
          <div className="dashboard-main">
            <div className="dashboard-column">
              {/* Quick Actions - Important for workflow */}
              <div className="quick-actions-section">
                <h2 className="section-title">Quick Actions</h2>
                <div className="quick-actions-grid">
                  {quickActionLinks.map((action, index) => (
                    <Link
                      to={action.path}
                      className="quick-action-card"
                      key={index}
                      style={{ borderLeftColor: action.color }}
                    >
                      <div
                        className="action-icon"
                        style={{ color: action.color }}
                      >
                        {action.icon}
                      </div>
                      <span>{action.title}</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Recent Projects Section */}
              <div className="recent-projects-wrapper">
                <h2 className="section-title">
                  <FolderOpen size={20} className="section-icon" />
                  My Recent Projects
                </h2>

                {loading ? (
                  <div className="loading-projects">
                    <div className="loading-spinner"></div>
                  </div>
                ) : projects.length > 0 ? (
                  <div className="projects-grid">
                    {projects.slice(0, 4).map((project) => (
                      <div
                        key={project.project_id}
                        className="project-card"
                        onClick={() => handleProjectClick(project.project_id)}
                      >
                        <div className="project-card-header">
                          <div className="project-icon">
                            <FolderOpen size={18} />
                          </div>
                          <span
                            className={`status-badge ${
                              project.status || "active"
                            }`}
                          >
                            {project.status || "Active"}
                          </span>
                        </div>

                        <h3 className="project-title">
                          {project.title || "Untitled Project"}
                        </h3>

                        <p className="project-description">
                          {project.description?.length > 70
                            ? project.description.substring(0, 70) + "..."
                            : project.description || "No description available"}
                        </p>

                        <div className="project-meta">
                          <div className="meta-item">
                            <Clock size={14} />
                            <span>
                              {new Date(
                                project.created_at
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        <div className="project-card-action">
                          <span>View Project</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-projects">
                    <FolderOpen size={32} />
                    <p>You don't have any projects yet</p>
                    <Link
                      to="/project_associate/create-project"
                      className="create-first-project-btn"
                    >
                      <Plus size={16} />
                      Create Your First Project
                    </Link>
                  </div>
                )}

                {projects.length > 0 && (
                  <div className="view-all-link">
                    <Link to="/project_associate/manage-projects">
                      View all projects
                    </Link>
                  </div>
                )}
              </div>
            </div>

            <div className="dashboard-column">
              {/* Approval Requests Section */}
              <div className="approval-status-wrapper">
                <h2 className="section-title">
                  <FileText size={20} className="section-icon" />
                  Recent Approval Submissions
                </h2>

                {loadingApprovalRequests ? (
                  <div className="loading-approvals">
                    <div className="loading-spinner"></div>
                  </div>
                ) : recentApprovalRequests.length > 0 ? (
                  <div className="approval-status-grid">
                    {recentApprovalRequests.map((request) => (
                      <div
                        key={request.id}
                        className="approval-status-card"
                        onClick={() => handleProjectClick(request.project_id)}
                      >
                        <div className="approval-card-header">
                          <div className="document-icon approval-icon-card">
                            <FileText size={18} />
                            <span className="document-type">
                              {request.document_type?.toUpperCase()}
                            </span>
                          </div>
                          <span className={`status-badge ${request.status}`}>
                            {request.status === "pending"
                              ? "Pending"
                              : request.status === "approved"
                              ? "Approved"
                              : "Rejected"}
                          </span>
                        </div>

                        <h3 className="project-title">
                          {request.project_name || "Untitled Project"}
                        </h3>

                        <div className="approval-meta">
                          <div className="meta-item">
                            <span className="meta-label">Submitted:</span>
                            <span className="meta-value">
                              {new Date(
                                request.created_at
                              ).toLocaleDateString()}
                            </span>
                          </div>

                          {request.admin_name && (
                            <div className="meta-item">
                              <span className="meta-label">Approved By:</span>
                              <span className="meta-value">
                                {request.admin_name}
                              </span>
                            </div>
                          )}
                        </div>

                        {request.comments && (
                          <div className="approval-comments">
                            <p>{request.comments}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-approvals">
                    <FileText size={32} />
                    <p>No approval submissions yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
